import React, { useMemo, useState } from 'react';
import { Badge, Button, Divider, Form, Popover, Select, Slider, Space, Row, Col, DatePicker, Checkbox } from 'antd';
import type { FormInstance } from 'antd';
import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

export type FilterSelectOption = {
  label: React.ReactNode;
  value: string | number | boolean;
  disabled?: boolean;
};

export type FilterSelectField = {
  name: string;
  type: string;
  label: React.ReactNode;
  placeholder?: string;
  options: FilterSelectOption[];
  allowClear?: boolean;
  showSearch?: boolean;
  mode?: 'multiple' | 'tags';
};

export type FilterPopoverProps = {
  fields: FilterSelectField[];
  onApply: (values: Record<string, any>) => void;
  onClear: () => void;
  initialValues?: Record<string, any>;
  appliedCount?: number;
  className?: string;
  placement?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'top' | 'topLeft' | 'topRight';
  form?: FormInstance;
  popoverWidth?: number | 'auto';
  columns?: 1 | 2 | 3;
};


const FilterPopover: React.FC<FilterPopoverProps> = ({
  fields, onApply, onClear, initialValues, appliedCount = 0, className, placement = 'bottomLeft',
  form: externalForm, popoverWidth = 'auto', columns }) => {

  const [open, setOpen] = useState(false);
  const [internalForm] = Form.useForm();
  const form = externalForm ?? internalForm;
  const allowClearByDefault = true;

  // Track live form values so buttons react to user input
  const watchedValues = Form.useWatch([], form);

  const isEmptyValue = (val: any) => {
    if (val === undefined || val === null) return true;
    if (Array.isArray(val)) return val.length === 0;
    if (typeof val === 'string') return val.trim() === '';
    return false;
  };

  const hasFilterValue = useMemo(() => {
    const vals = watchedValues ?? {};
    return fields.some((f) => !isEmptyValue(vals[f.name]));
  }, [watchedValues, fields]);

  // Allow clearing when filters are already applied, even if form is untouched
  const canClear = hasFilterValue || appliedCount > 0;

  const content = useMemo(() => {
    const fieldCount = fields.length;
    let columnCount: number;
    if (columns) {
      columnCount = columns;
    } else {
      if (fieldCount <= 3) {
        columnCount = 1;
      } else if (fieldCount <= 6) {
        columnCount = 2;
      } else {
        columnCount = 3;
      }
    }

    const colSpan = 24 / columnCount;
    const calculatedWidth = popoverWidth === 'auto'
      ? (columnCount === 1 ? 320 : columnCount === 2 ? 520 : 720)
      : popoverWidth;

    const hasInitialValues = Object.keys(initialValues ?? {}).length > 0;


    return (
      <div className={className ? `${className} filter-popover` : 'filter-popover'} style={{ width: calculatedWidth, maxWidth: '90vw' }}>
        <Form form={form} layout="vertical" onFinish={(vals) => { onApply(vals ?? {}); setOpen(false); }}>
          <Row gutter={[16, 0]}>
            {fields.map((f, idx) => (
              <Col span={colSpan} key={f.name}>
                <Form.Item label={f.label} name={f.name} style={{ marginBottom: '16px' }}>
                  {f.type === 'select' ? (
                    <Select
                      placeholder={f.placeholder}
                      allowClear={f.allowClear ?? allowClearByDefault}
                      showSearch={f.showSearch ?? false}

                      filterOption={(input, option) =>
                        (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      className="filter-select"
                      options={f.options}
                    />
                  ) : f.type === 'checkbox' ? (
                    <Checkbox.Group options={f.options} />
                  ) : f.type === 'date' ? (
                    <DatePicker style={{ width: '100%' }} allowClear />
                  ) : f.type === 'slider' ? (
                    <Slider range min={1000} max={5000} defaultValue={[1000, 5000]} />
                  ) : (
                    <Select
                      placeholder={f.placeholder}
                      allowClear={f.allowClear ?? allowClearByDefault}
                      className="filter-select"
                      options={f.options}
                    />
                  )}
                </Form.Item>
              </Col>
            ))}
          </Row>

          <Divider style={{ margin: '8px 0 16px' }} />

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
              disabled={!canClear}
              onClick={() => { form.resetFields(); onClear(); setOpen(false); }}
            >
              Clear
            </Button>
            <Button type="primary" htmlType="submit" disabled={!hasFilterValue}>
              Apply
            </Button>
          </Space>
        </Form>
      </div>
    );
  }, [className, fields, form, initialValues, onApply, onClear, setOpen, popoverWidth, columns, allowClearByDefault, hasFilterValue, canClear]);

  return (
    <Popover
      content={content}
      placement={placement}
      trigger="click"
      open={open}
      overlayStyle={{ maxHeight: '80vh', overflow: 'auto' }}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          // Ensure date fields are passed as dayjs objects so DatePicker shows them
          const initVals: Record<string, any> = { ...(initialValues ?? {}) };
          fields.forEach(f => {
            if (f.type === 'date' && initVals[f.name]) {
              try {
                initVals[f.name] = dayjs(initVals[f.name]);
              } catch (e) {
                // fallback: leave as-is
              }
            }
          });
          form.setFieldsValue(initVals);
        }
      }}
    >
      <span style={{ cursor: 'pointer', marginLeft: '10px' }}>
        {appliedCount > 0 ? (
          <Badge dot>
            <FilterFilled style={{ color: '#4f46e5', fontSize: 18 }} />
          </Badge>
        ) : (
          <FilterOutlined style={{ fontSize: 18 }} />
        )}
      </span>
    </Popover>
  );
};

export default FilterPopover;
